import { generatePath } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { RawTransactionKey } from 'api/Transaction/Types/RawTransactionDto'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { isTransactionPendingStatus } from 'utils/transaction/status.index'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const transactionOverviewWalletOrClientColumn: GridColumn[] = [
  {
    title: 'Reference',
    field: RawTransactionKey.reference,
    resizable: true,
    width: 200,
    minResizableWidth: 200,
    show: true,
    sortable: true,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.referenceId) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath('/transactions/:id', {
                id: props.dataItem.id,
              }),
            }}
          >
            {props.dataItem.referenceId}
          </Link>
        </td>
      )
    },
  },
  {
    title: 'Type',
    field: RawTransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.type?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.type.name}
        </td>
      )
    },
    resizable: true,
    width: 150,
    minResizableWidth: 150,
    show: true,
    sortable: true,
    className: 'table-columns__content',
  },
  {
    title: 'State',
    field: RawTransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem.state
      const transactionType = props.dataItem.type

      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      if (
        isTransactionPendingStatus(status.id) &&
        transactionType.id === TransactionType.WalletWithdrawal
      ) {
        return (
          <td
            className={generateClassNameForReverseType(props.dataItem)}
          >{`${status.name} on PSP side`}</td>
        )
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    width: 150,
    minResizableWidth: 150,
    show: true,
    sortable: true,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Date',
    field: RawTransactionKey.date,
    resizable: true,
    width: 100,
    minResizableWidth: 100,
    show: true,
    sortable: true,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.date) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {formatLocalDateUTC(props.dataItem.date)}
        </td>
      )
    },
  },
  {
    title: 'Client',
    field: RawTransactionKey.client,
    resizable: true,
    width: 200,
    minResizableWidth: 200,
    show: false,
    sortable: false,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.client?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: props.dataItem.client.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.client.name}
          </Link>
        </td>
      )
    },
  },
  {
    title: 'Wallet',
    field: RawTransactionKey.wallet,
    resizable: true,
    width: 200,
    minResizableWidth: 200,
    show: true,
    sortable: false,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.wallet?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionDepositWithdrawal.url }}
            to={{
              pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                id: props.dataItem.wallet.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.wallet.name}
          </Link>
        </td>
      )
    },
  },
  {
    title: 'Trading Account',
    field: RawTransactionKey.tradingAccount,
    resizable: true,
    width: 200,
    minResizableWidth: 200,
    show: true,
    sortable: false,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccount = props.dataItem.tradingAccount
      if (!tradingAccount?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionDepositWithdrawal.url }}
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccount.id,
                slug: 'details',
              }),
            }}
          >
            {tradingAccount.name}
          </Link>
        </td>
      )
    },
  },
  {
    title: 'Payment Provider',
    field: RawTransactionKey.paymentProvider,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.paymentProvider?.name) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const paymentProviderName = props.dataItem?.paymentProvider?.name
      const paymentProviderExternalName = props.dataItem?.paymentProvider?.externalName

      if (paymentProviderExternalName) {
        return (
          <td className={generateClassNameForReverseType(props.dataItem)}>
            <Tooltip key='top'>
              <TooltipTrigger render={<span />}>{paymentProviderName}</TooltipTrigger>
              <TooltipContent side='top'>
                <p>{paymentProviderExternalName}</p>
              </TooltipContent>
            </Tooltip>
          </td>
        )
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>{paymentProviderName}</td>
      )
    },
    width: 150,
    minResizableWidth: 150,
    resizable: true,
    show: true,
    sortable: false,
    className: 'table-columns__content',
  },
  {
    title: 'Amount',
    field: RawTransactionKey.amount,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.amount) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.amount.toFixed(2)}
        </td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: true,
    sortable: false,
    show: true,
  },
  {
    title: 'Direction',
    field: RawTransactionKey.directionType,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.amount) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.amount.toFixed(2)}
        </td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: true,
    show: false,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'CCY',
    field: RawTransactionKey.currency,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.currency?.id) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.currency.id}
        </td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: true,
    show: false,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'CCYAmount',
    field: RawTransactionKey.currencyAmount,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.currencyAmount) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.currencyAmount.toFixed(2)}
        </td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: false,
    show: true,
    sortable: false,
    className: 'table-columns__number',
  },
  {
    title: 'Rate',
    field: RawTransactionKey.rate,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.rate) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>{props.dataItem.rate}</td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: true,
    show: false,
    sortable: false,
    className: 'table-columns__number',
  },
  {
    title: 'External Reference',
    field: RawTransactionKey.externalReference,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.externalReference) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.externalReference}
        </td>
      )
    },
    width: 150,
    minResizableWidth: 150,
    resizable: true,
    show: false,
    sortable: false,
    className: 'table-columns__content',
  },
  {
    title: 'External CCY',
    field: RawTransactionKey.paymentProviderCurrency,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.paymentProviderCurrency?.id) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.paymentProviderCurrency.id}
        </td>
      )
    },
    width: 100,
    minResizableWidth: 100,
    resizable: true,
    show: true,
    sortable: false,
    className: 'table-columns__content',
  },
  {
    title: 'Comment',
    field: RawTransactionKey.comment,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.comment) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.comment}
        </td>
      )
    },
    width: 200,
    minResizableWidth: 200,
    resizable: true,
    show: true,
    sortable: true,
    className: 'table-columns__content',
  },
  {
    title: 'Date created',
    field: RawTransactionKey.createdDate,
    width: 150,
    minResizableWidth: 150,
    resizable: true,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.createdDate) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {formatLocalDateUTC(props.dataItem.createdDate, true)}
        </td>
      )
    },
    sortable: true,
    show: true,
  },
  {
    title: 'Date updated',
    field: RawTransactionKey.updatedDate,
    width: 150,
    minResizableWidth: 150,
    resizable: true,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.updatedDate) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {formatLocalDateUTC(props.dataItem.updatedDate, true)}
        </td>
      )
    },
    sortable: true,
    show: true,
  },
]

export default transactionOverviewWalletOrClientColumn
